import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Aperture, Check, Facebook, Instagram } from "lucide-react";
import { useTranslation } from "react-i18next";
import { scrollTo } from "../components/Nav";
import { BeforeAfter } from "../components/BeforeAfter";
import { Portfolio } from "../components/Portfolio";
import { Pricing } from "../components/Pricing";
import { Booking } from "../components/Booking";
import { Reveal } from "../components/Reveal";
import { useTheme } from "../lib/theme";
import { trackEvent } from "../lib/analytics";
import { FACEBOOK_URL, INSTAGRAM_URL } from "../lib/social";

const HERO_IMG = "https://images.unsplash.com/photo-1741850419928-aa65b3d5e3a6?fm=jpg&q=80&w=1920&auto=format&fit=crop";
const SERVICE_IMGS = {
  foto: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?fm=jpg&q=80&w=1600&auto=format&fit=crop",
  drone: "https://images.unsplash.com/photo-1416331108676-a22ccb276e35?fm=jpg&q=80&w=1600&auto=format&fit=crop&bri=18&sat=8",
  tour: "/assets/tour3d-dollhouse.jpg",
};

export default function Landing() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const badges = t("lp.badges", { returnObjects: true });
  const problems = t("lp.problems", { returnObjects: true });
  const services = t("lp.services", { returnObjects: true });
  const guarantees = t("lp.guarantees", { returnObjects: true });
  const howSteps = t("lp.howSteps", { returnObjects: true });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = t("lp.title");
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("lp.meta"));
    // Parametri UTM della campagna Meta: salvati e allegati alla richiesta preventivo
    const q = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content"].forEach((k) => {
      const v = q.get(k);
      if (v) utm[k] = v;
    });
    if (Object.keys(utm).length) sessionStorage.setItem("loi-utm", JSON.stringify(utm));
    trackEvent("landing_view", { page: "fotografia-case-vacanze", language: i18n.language, ...utm });
    return () => {
      document.title = t("local.defaultTitle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className={`bg-[#0D0F12]${theme === "light" ? " light" : ""}`} data-testid="landing-page">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0D0F12]/70 backdrop-blur-xl" data-testid="landing-nav">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" className="flex shrink-0 items-center gap-2.5" data-testid="landing-logo">
            <Aperture className="h-5 w-5 text-[#D6705A]" strokeWidth={1.5} />
            <span className="whitespace-nowrap font-display text-lg tracking-wide text-[#F9F8F6]">
              L&rsquo;Obiettivo <span className="italic text-[#D6705A]">Immobiliare</span>
            </span>
          </Link>
          <button
            onClick={() => scrollTo("#prenota")}
            className="whitespace-nowrap rounded-full bg-[#D6705A] px-6 py-2.5 font-code text-[11px] uppercase tracking-[0.2em] text-[#0D0F12] transition-[background-color,transform] duration-300 hover:bg-[#F9F8F6] hover:-translate-y-0.5"
            data-testid="landing-nav-cta"
          >
            {t("lp.ctaShort")}
          </button>
        </div>
      </header>

      <section className="relative flex min-h-[100svh] items-center overflow-hidden grain" data-testid="landing-hero">
        <img src={HERO_IMG} alt={t("lp.imgAlt")} className="absolute inset-0 h-full w-full object-cover" data-testid="landing-hero-image" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0F12] via-transparent to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-32 lg:px-12">
          <Reveal>
            <p className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]" data-testid="landing-overline">
              {t("lp.overline")}
            </p>
            <h1 className="mt-5 max-w-4xl font-display text-4xl font-light leading-[1.02] tracking-tight text-[#F9F8F6] sm:text-6xl lg:text-7xl" data-testid="landing-heading">
              {t("lp.h1pre")} <em className="italic text-[#D6705A]">{t("lp.h1em")}</em> {t("lp.h1post")}
            </h1>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-[#C9C7C3] md:text-base" data-testid="landing-sub">
              {t("lp.sub")}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollTo("#prenota")}
                className="rounded-full bg-[#D6705A] px-9 py-4 font-code text-[11px] uppercase tracking-[0.25em] text-[#0D0F12] transition-[background-color,transform] duration-300 hover:bg-[#F9F8F6] hover:-translate-y-1"
                data-testid="landing-cta-primary"
              >
                {t("lp.ctaPrimary")}
              </button>
              <button
                onClick={() => scrollTo("#portfolio")}
                className="rounded-full border border-white/25 px-8 py-4 font-code text-[11px] uppercase tracking-[0.25em] text-[#F9F8F6] transition-[border-color,transform] duration-300 hover:border-[#D6705A] hover:-translate-y-1"
                data-testid="landing-cta-secondary"
              >
                {t("lp.ctaSecondary")}
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3" data-testid="landing-badges">
              {badges.map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-sm text-[#C9C7C3]">
                  <Check className="h-4 w-4 text-[#D6705A]" /> {b}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Problemi → Conseguenza → Soluzione */}
      <section className="border-t border-white/5 bg-[#0D0F12] px-6 py-24 lg:px-12" data-testid="landing-problems">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]">{t("lp.problemsOverline")}</p>
            <h2 className="mt-4 max-w-3xl font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl" data-testid="landing-problems-heading">
              {t("lp.problemsH2pre")} <em className="italic text-[#D6705A]">{t("lp.problemsH2em")}</em>{t("lp.problemsH2post")}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2" data-testid="landing-problems-grid">
            {problems.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="flex h-full flex-col bg-[#0D0F12] p-8" data-testid={`landing-problem-${i}`}>
                  <span className="font-code text-[10px] tracking-[0.25em] text-[#D6705A]">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-2xl font-light text-[#F9F8F6]">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#A0A0A0]">{c.problem}</p>
                  <p className="mt-5 flex items-start gap-2.5 border-t border-white/10 pt-5 text-sm leading-relaxed text-[#C9C7C3]">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D6705A]" /> {c.solution}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-[#D6705A]/30 bg-[#D6705A]/5 px-8 py-7" data-testid="landing-problems-cta">
              <p className="max-w-xl font-display text-xl font-light leading-snug text-[#F9F8F6] sm:text-2xl">
                {t("lp.problemsCta")}
              </p>
              <button
                onClick={() => scrollTo("#prenota")}
                className="shrink-0 rounded-full bg-[#D6705A] px-8 py-4 font-code text-[11px] uppercase tracking-[0.25em] text-[#0D0F12] transition-[background-color,transform] duration-300 hover:bg-[#F9F8F6] hover:-translate-y-1"
                data-testid="landing-problems-button"
              >
                {t("lp.ctaPrimary")}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Servizi spiegati */}
      <section className="bg-[#0D0F12] px-6 py-24 lg:px-12 lg:py-32" data-testid="landing-services">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]">{t("lp.servOverline")}</p>
            <h2 className="mt-4 max-w-3xl font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl" data-testid="landing-services-heading">
              {t("lp.servH2pre")} <em className="italic text-[#D6705A]">{t("lp.servH2em")}</em>{t("lp.servH2post")}
            </h2>
          </Reveal>
          <div className="mt-16 space-y-20">
            {services.map((s, i) => (
              <Reveal key={s.key}>
                <div className="grid items-center gap-10 lg:grid-cols-2" data-testid={`landing-service-${s.key}`}>
                  <div className={i % 2 ? "lg:order-2" : ""}>
                    <img
                      src={SERVICE_IMGS[s.key]}
                      alt={s.imgAlt}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[4/3] w-full object-cover"
                      data-testid={`landing-service-img-${s.key}`}
                    />
                  </div>
                  <div className={i % 2 ? "lg:order-1" : ""}>
                    <h3 className="font-display text-3xl font-light text-[#F9F8F6] sm:text-4xl">{s.title}</h3>
                    <p className="mt-4 font-display text-lg font-light italic leading-snug text-[#C9C7C3] sm:text-xl">{s.lead}</p>
                    {s.text && (
                      <p className="mt-4 text-sm leading-relaxed text-[#A0A0A0]" data-testid={`landing-service-text-${s.key}`}>{s.text}</p>
                    )}
                    <ul className="mt-6 space-y-3">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-[#A0A0A0]">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D6705A]" /> {p}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => scrollTo("#prenota")}
                      className="mt-8 flex items-center gap-2 font-code text-[11px] uppercase tracking-[0.25em] text-[#D6705A] transition-colors duration-300 hover:text-[#F9F8F6]"
                      data-testid={`landing-service-cta-${s.key}`}
                    >
                      {t("lp.ctaPrimary")} <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.05}>
            <p className="mt-20 max-w-2xl font-display text-2xl font-light italic leading-snug text-[#F9F8F6] sm:text-3xl" data-testid="landing-services-closing">
              {t("lp.servClosing")}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="mt-12 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/10 pt-10" data-testid="landing-guarantees">
              {guarantees.map((g) => (
                <li key={g} className="flex items-center gap-2.5 text-sm text-[#C9C7C3]">
                  <Check className="h-4 w-4 text-[#D6705A]" /> {g}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <Pricing />
      <BeforeAfter />
      <Portfolio />

      {/* Come funziona */}
      <section className="border-t border-white/5 bg-[#0D0F12] px-6 py-24 lg:px-12" data-testid="landing-how">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl" data-testid="landing-how-heading">
              {t("lp.howTitle")}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-testid="landing-how-grid">
            {howSteps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`landing-how-${i}`}>
                  <span className="font-code text-[11px] tracking-[0.3em] text-[#D6705A]">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-xl font-light text-[#F9F8F6]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#A0A0A0]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Intro configuratore */}
      <section className="border-t border-white/5 bg-[#0D0F12] px-6 pt-24 lg:px-12" data-testid="landing-calc-intro">
        <Reveal className="mx-auto max-w-7xl">
          <h2 className="max-w-2xl font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl" data-testid="landing-calc-heading">
            {t("lp.calcTitle")}
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#A0A0A0] md:text-base">
            {t("lp.calcSub")}
          </p>
        </Reveal>
      </section>

      <Booking origin="landing" />

      <footer className="border-t border-white/5 bg-[#0D0F12] px-6 py-10 lg:px-12" data-testid="landing-footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <a
            href="mailto:info@lobiettivoimmobiliare.it"
            className="font-code text-[11px] uppercase tracking-[0.25em] text-[#A0A0A0] transition-colors duration-300 hover:text-[#F9F8F6]"
            data-testid="landing-email"
          >
            info@lobiettivoimmobiliare.it
          </a>
          <div className="flex items-center gap-5">
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[#A0A0A0] transition-colors duration-300 hover:text-[#D6705A]" data-testid="landing-instagram-link">
              <Instagram className="h-4 w-4" />
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-[#A0A0A0] transition-colors duration-300 hover:text-[#D6705A]" data-testid="landing-facebook-link">
              <Facebook className="h-4 w-4" />
            </a>
          </div>
          <Link
            to="/"
            className="font-code text-[11px] uppercase tracking-[0.25em] text-[#A0A0A0] transition-colors duration-300 hover:text-[#F9F8F6]"
            data-testid="landing-site-link"
          >
            {t("lp.siteLink")} ↗
          </Link>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("loi:open-cookie-preferences"))}
            className="font-code text-[10px] uppercase tracking-[0.25em] text-[#666] transition-colors duration-300 hover:text-[#A0A0A0]"
            data-testid="landing-cookie-preferences-link"
          >
            {t("consent.footerLink")}
          </button>
          <p className="font-code text-[10px] uppercase tracking-[0.25em] text-[#666]">
            © {new Date().getFullYear()} L'Obiettivo Immobiliare — Sicilia
          </p>
        </div>
      </footer>
    </div>
  );
}
